"use client";

import {
	AlertTriangle,
	Check,
	FileDown,
	FolderOpen,
	Gauge,
	HardDrive,
	Loader2,
	RefreshCcw,
	ShieldCheck,
	Sparkles,
	Trash2,
	UploadCloud,
} from "lucide-react";
import {
	type ChangeEvent,
	type DragEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { useLocale, useTranslations } from "next-intl";
import { compressVideo, type CompressOptions } from "@/lib/ffmpeg";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/utils/ui";

type ToolPhase =
	| "empty"
	| "inspecting"
	| "ready"
	| "unsupported"
	| "processing"
	| "success"
	| "partial"
	| "failed"
	| "cancelled";
type CompressionGoal = "quality" | "balanced" | "smaller" | "target";

const COMPRESSION_GOALS: readonly {
	key: CompressionGoal;
	icon: typeof Sparkles;
}[] = [
	{ key: "quality", icon: Sparkles },
	{ key: "balanced", icon: Gauge },
	{ key: "smaller", icon: FileDown },
	{ key: "target", icon: HardDrive },
];

const RESOLUTION_OPTIONS = [
	{ value: "original", height: Number.POSITIVE_INFINITY },
	{ value: "1080p", height: 1080 },
	{ value: "720p", height: 720 },
	{ value: "480p", height: 480 },
] as const;

function formatBytes({
	bytes,
	locale,
}: {
	bytes: number;
	locale: string;
}): string {
	if (bytes < 1024) return `${new Intl.NumberFormat(locale).format(bytes)} B`;
	const units = ["KB", "MB", "GB"];
	let value = bytes / 1024;
	let unit = units[0];
	for (let index = 1; value >= 1024 && index < units.length; index += 1) {
		value /= 1024;
		unit = units[index];
	}
	return `${new Intl.NumberFormat(locale, {
		maximumFractionDigits: value >= 10 ? 0 : 1,
	}).format(value)} ${unit}`;
}

function formatDuration(seconds: number): string {
	const rounded = Math.max(0, Math.round(seconds));
	const hours = Math.floor(rounded / 3600);
	const minutes = Math.floor((rounded % 3600) / 60);
	const remainingSeconds = rounded % 60;
	return hours > 0
		? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
		: `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function VideoCompressor() {
	const locale = useLocale();
	const t = useTranslations("VideoCompressor.tool");
	const inputRef = useRef<HTMLInputElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const videoUrlRef = useRef<string | null>(null);
	const compressButtonRef = useRef<HTMLButtonElement>(null);
	const [file, setFile] = useState<File | null>(null);
	const [videoUrl, setVideoUrl] = useState<string | null>(null);
	const [phase, setPhase] = useState<ToolPhase>("empty");
	const [isDragging, setIsDragging] = useState(false);
	const [fileError, setFileError] = useState<string | null>(null);
	const [duration, setDuration] = useState(0);
	const [sourceSize, setSourceSize] = useState({ width: 0, height: 0 });
	const [goal, setGoal] = useState<CompressionGoal>("balanced");
	const [targetSize, setTargetSize] = useState("25");
	const [resolution, setResolution] =
		useState<CompressOptions["resolution"]>("original");
	const [keepAudio, setKeepAudio] = useState(true);
	const [progress, setProgress] = useState({ percent: 0, pass: 1 as 1 | 2 });
	const [resultBlob, setResultBlob] = useState<Blob | null>(null);
	const [resultUrl, setResultUrl] = useState<string | null>(null);
	const [resultSize, setResultSize] = useState(0);
	const [resultResolution, setResultResolution] = useState({
		width: 0,
		height: 0,
	});
	const [errorCategory, setErrorCategory] = useState<
		"input" | "device" | "target" | "unknown"
	>("unknown");
	const abortRef = useRef<AbortController | null>(null);

	useEffect(
		() => () => {
			if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		},
		[],
	);

	const resetFile = () => {
		if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		videoUrlRef.current = null;
		setVideoUrl(null);
		setFile(null);
		setPhase("empty");
		setFileError(null);
		setDuration(0);
		setSourceSize({ width: 0, height: 0 });
		setGoal("balanced");
		setTargetSize("25");
		setResolution("original");
		setKeepAudio(true);
		setResultBlob(null);
		if (resultUrl) {
			URL.revokeObjectURL(resultUrl);
			setResultUrl(null);
		}
		setResultSize(0);
		setResultResolution({ width: 0, height: 0 });
		setErrorCategory("unknown");
		if (abortRef.current) {
			abortRef.current.abort();
			abortRef.current = null;
		}
		if (inputRef.current) inputRef.current.value = "";
	};

	const chooseFile = (nextFile?: File) => {
		if (!nextFile) return;
		setFileError(null);

		if (!nextFile.type.startsWith("video/")) {
			setFileError(t("errors.invalidFile"));
			return;
		}

		if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		const nextUrl = URL.createObjectURL(nextFile);
		videoUrlRef.current = nextUrl;
		setVideoUrl(nextUrl);
		setFile(nextFile);
		setDuration(0);
		setSourceSize({ width: 0, height: 0 });
		setResolution("original");
		setPhase("inspecting");
	};

	const handleCompress = async () => {
		if (!file || phase !== "ready") return;

		console.log("[Video Compressor] Starting compression", {
			fileName: file.name,
			fileSize: file.size,
			goal,
			targetSize: goal === "target" ? targetSize : undefined,
			resolution,
			keepAudio,
		});

		setPhase("processing");
		setProgress({ percent: 0, pass: 1 });
		setErrorCategory("unknown");

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			console.log("[Video Compressor] Loading FFmpeg...");
			const { loadFFmpeg } = await import("@/lib/ffmpeg");
			await loadFFmpeg();
			console.log("[Video Compressor] FFmpeg loaded successfully");

			// Use duration from video element (already available in ready state)
			const videoDuration = duration;
			console.log(
				"[Video Compressor] Video duration from metadata:",
				videoDuration,
				"seconds",
			);

			const targetBytes =
				goal === "target" ? targetNumber * 1024 * 1024 : undefined;
			if (targetBytes) {
				console.log(
					"[Video Compressor] Target size:",
					targetBytes,
					"bytes (",
					targetSize,
					"MB)",
				);
			}

			console.log("[Video Compressor] Starting compression...");
			const blob = await compressVideo({
				file,
				options: {
					goal,
					targetBytes,
					resolution,
					keepAudio,
					durationSeconds: videoDuration,
				},
				onProgress: ({ percent, pass }) => {
					console.log(`[Video Compressor] Progress: Pass ${pass}, ${percent}%`);
					setProgress({ percent, pass });
				},
				signal: controller.signal,
			});

			console.log(
				"[Video Compressor] Compression complete. Blob size:",
				blob.size,
				"bytes",
			);
			setResultBlob(blob);
			setResultSize(blob.size);

			const url = URL.createObjectURL(blob);
			setResultUrl(url);
			console.log("[Video Compressor] Result URL created");

			const video = document.createElement("video");
			video.src = url;
			video.preload = "metadata";

			const previewTimeout = setTimeout(() => {
				console.log(
					"[Video Compressor] Preview timeout - falling back to partial success",
				);
				setPhase("partial");
			}, 1500);

			video.onloadedmetadata = () => {
				clearTimeout(previewTimeout);
				setResultResolution({
					width: video.videoWidth,
					height: video.videoHeight,
				});
				console.log(
					"[Video Compressor] Preview loaded successfully:",
					video.videoWidth,
					"x",
					video.videoHeight,
				);
				setPhase("success");
			};

			video.onerror = () => {
				clearTimeout(previewTimeout);
				console.error(
					"[Video Compressor] Preview load error - falling back to partial success",
				);
				setPhase("partial");
			};
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			console.error("[Video Compressor] Error:", message, error);

			if (message.includes("cancelled")) {
				console.log("[Video Compressor] Compression cancelled by user");
				setPhase("cancelled");
			} else if (message.includes("target infeasible")) {
				console.log("[Video Compressor] Target size infeasible");
				setErrorCategory("target");
				setPhase("failed");
			} else if (
				message.includes("out of memory") ||
				message.includes("Cannot enlarge memory") ||
				message.includes("OOM")
			) {
				console.log("[Video Compressor] Device out of memory");
				setErrorCategory("device");
				setPhase("failed");
			} else if (
				message.includes("Invalid data") ||
				message.includes("moov atom not found") ||
				message.includes("could not read")
			) {
				console.log("[Video Compressor] Invalid input data");
				setErrorCategory("input");
				setPhase("failed");
			} else if (message.includes("FFmpeg not loaded")) {
				console.log("[Video Compressor] FFmpeg not loaded error");
				setErrorCategory("device");
				setPhase("failed");
			} else {
				console.log("[Video Compressor] Unknown error");
				setErrorCategory("unknown");
				setPhase("failed");
			}
		} finally {
			abortRef.current = null;
		}
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		chooseFile(event.target.files?.[0]);
		event.target.value = "";
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
		chooseFile(event.dataTransfer.files[0]);
	};

	const handleMetadata = () => {
		const video = videoRef.current;
		if (
			!video ||
			!Number.isFinite(video.duration) ||
			video.duration <= 0 ||
			video.videoWidth <= 0 ||
			video.videoHeight <= 0
		) {
			setPhase("unsupported");
			setFileError(t("errors.metadata"));
			return;
		}

		setDuration(video.duration);
		setSourceSize({ width: video.videoWidth, height: video.videoHeight });
		setPhase("ready");
	};

	const handleVideoError = () => {
		setPhase("unsupported");
		setFileError(t("errors.preview"));
	};

	const targetNumber = Number(targetSize);
	const targetBytes = targetNumber * 1024 * 1024;
	const hasTargetError =
		goal === "target" &&
		(targetSize.trim() === "" ||
			!Number.isFinite(targetNumber) ||
			targetNumber <= 0 ||
			(file ? targetBytes >= file.size : false));
	const targetError = hasTargetError
		? file && Number.isFinite(targetNumber) && targetNumber > 0
			? t("target.belowOriginal", {
					size: formatBytes({ bytes: file.size, locale }),
				})
			: t("target.positive")
		: null;
	const hasQualityWarning =
		goal === "target" &&
		file !== null &&
		!hasTargetError &&
		targetBytes / file.size < 0.25;
	const isReady = phase === "ready" && !hasTargetError;
	const disabledReason =
		phase === "inspecting"
			? t("status.wait")
			: phase === "unsupported"
				? t("status.chooseSupported")
				: hasTargetError
					? t("status.fixTarget")
					: null;

	return (
		<section
			aria-label={t("ariaLabel")}
			className="mx-auto w-full max-w-[84rem]"
		>
			<input
				ref={inputRef}
				type="file"
				accept="video/*"
				className="sr-only"
				onChange={handleInputChange}
			/>
			<div className="overflow-hidden rounded-[2rem] border border-blue-200/80 bg-slate-50/90 shadow-[0_28px_80px_-42px_rgba(37,99,235,0.45)] backdrop-blur-xl dark:border-blue-900/70 dark:bg-slate-950/88 dark:shadow-[0_28px_80px_-42px_rgba(34,211,238,0.22)]">
				<div className="flex flex-col gap-3 border-b border-blue-100 bg-white/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 dark:border-blue-950 dark:bg-slate-950/80">
					<div>
						<h2 className="font-bold tracking-tight">{t("header.title")}</h2>
						<p className="text-muted-foreground mt-0.5 text-sm">
							{t("header.description")}
						</p>
					</div>
					<span className="inline-flex min-h-8 w-fit items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3 text-xs font-semibold text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/50 dark:text-cyan-100">
						<ShieldCheck className="size-3.5" aria-hidden="true" />
						{t("header.local")}
					</span>
				</div>

				<div className="p-4 sm:p-6 lg:p-8">
					{!file ? (
						<>
							<div
								className={cn(
									"relative flex min-h-80 flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-white/90 px-6 text-center shadow-inner transition-colors dark:bg-slate-900/80",
									isDragging
										? "border-cyan-400 bg-cyan-50/80 dark:border-cyan-400 dark:bg-cyan-950/30"
										: "border-blue-200 hover:border-blue-400 dark:border-blue-900 dark:hover:border-cyan-600",
								)}
								onDragEnter={(event) => {
									event.preventDefault();
									setIsDragging(true);
								}}
								onDragOver={(event) => event.preventDefault()}
								onDragLeave={(event) => {
									if (
										!(event.relatedTarget instanceof Node) ||
										!event.currentTarget.contains(event.relatedTarget)
									) {
										setIsDragging(false);
									}
								}}
								onDrop={handleDrop}
							>
								<div className="flex size-16 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20">
									<UploadCloud className="size-7" aria-hidden="true" />
								</div>
								<p className="mt-6 text-2xl font-bold tracking-tight">
									{t("drop.title")}
								</p>
								<Button
									className="mt-6 h-12 rounded-xl bg-blue-600 px-6 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500"
									onClick={() => inputRef.current?.click()}
								>
									<FolderOpen className="size-4.5" aria-hidden="true" />
									{t("drop.choose")}
								</Button>
								<p className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
									<ShieldCheck
										className="size-3.5 text-cyan-600"
										aria-hidden="true"
									/>
									{t("drop.privacy")}
								</p>
							</div>
							{fileError ? (
								<p
									className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
									role="alert"
								>
									<AlertTriangle
										className="mt-0.5 size-4 shrink-0"
										aria-hidden="true"
									/>
									{fileError}
								</p>
							) : null}
						</>
					) : (
						<div className="grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(22rem,0.95fr)]">
							<div className="min-w-0 space-y-4">
								<div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm sm:p-5 dark:border-blue-950 dark:bg-slate-900">
									<div className="mb-4 flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="truncate font-semibold">{file.name}</p>
											<p className="text-muted-foreground mt-1 text-xs">
												{formatBytes({ bytes: file.size, locale })}
												{sourceSize.width
													? ` · ${sourceSize.width} × ${sourceSize.height}`
													: ""}
												{duration ? ` · ${formatDuration(duration)}` : ""}
											</p>
										</div>
										<div className="flex shrink-0 gap-1">
											<Button
												variant="ghost"
												className="h-10 rounded-xl px-3"
												onClick={() => inputRef.current?.click()}
												aria-label={t("file.replace")}
											>
												<RefreshCcw className="size-4" aria-hidden="true" />
												<span className="hidden sm:inline">
													{t("file.replace")}
												</span>
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="size-10 rounded-xl"
												onClick={resetFile}
												aria-label={t("file.remove")}
											>
												<Trash2 className="size-4" aria-hidden="true" />
											</Button>
										</div>
									</div>

									<div className="relative flex aspect-video min-h-56 items-center justify-center overflow-hidden rounded-2xl bg-slate-950">
										{phase === "inspecting" ? (
											<div
												className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-950 text-slate-200"
												role="status"
											>
												<Loader2
													className="size-6 animate-spin motion-reduce:animate-none"
													aria-hidden="true"
												/>
												{t("file.inspecting")}
											</div>
										) : null}
										{phase === "unsupported" ? (
											<div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-200">
												<AlertTriangle
													className="size-7 text-amber-300"
													aria-hidden="true"
												/>
												<p className="max-w-md text-sm leading-6">
													{fileError}
												</p>
											</div>
										) : null}
										{/* User-selected local videos do not provide a captions track. */}
										{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
										<video
											ref={videoRef}
											src={videoUrl ?? undefined}
											controls
											preload="metadata"
											className={cn(
												"h-full w-full object-contain transition-opacity",
												phase !== "ready" && "opacity-0",
											)}
											onLoadedMetadata={handleMetadata}
											onError={handleVideoError}
										/>
									</div>
									{phase === "ready" && fileError ? (
										<p
											className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
											role="alert"
										>
											<AlertTriangle
												className="mt-0.5 size-4 shrink-0"
												aria-hidden="true"
											/>
											{fileError}
										</p>
									) : null}
								</div>

								<div className="grid grid-cols-3 gap-2 rounded-2xl border border-slate-200 bg-slate-100/80 p-3 text-center text-xs dark:border-slate-800 dark:bg-slate-900/70">
									<div>
										<span className="text-muted-foreground block">
											{t("metrics.original")}
										</span>
										<strong className="mt-1 block">
											{formatBytes({ bytes: file.size, locale })}
										</strong>
									</div>
									<div>
										<span className="text-muted-foreground block">
											{t("metrics.output")}
										</span>
										<strong className="mt-1 block">MP4</strong>
									</div>
									<div>
										<span className="text-muted-foreground block">
											{t("metrics.target")}
										</span>
										<strong className="mt-1 block">
											{goal === "target"
												? `${targetSize || "—"} MB`
												: t(`goals.${goal}`)}
										</strong>
									</div>
								</div>
							</div>

							<div className="min-w-0 rounded-2xl border border-blue-100 bg-white p-5 shadow-sm sm:p-6 dark:border-blue-950 dark:bg-slate-900">
								<fieldset disabled={phase !== "ready"}>
									<legend className="text-base font-bold">
										{t("goals.title")}
									</legend>
									<div className="mt-4 grid grid-cols-2 gap-2.5">
										{COMPRESSION_GOALS.map((item) => {
											const Icon = item.icon;
											const selected = goal === item.key;
											return (
												<label
													key={item.key}
													className={cn(
														"focus-within:ring-ring flex min-h-20 cursor-pointer flex-col justify-between rounded-xl border p-3 transition-colors focus-within:ring-2",
														selected
															? "border-blue-500 bg-blue-50 text-blue-950 ring-1 ring-blue-500/15 dark:border-cyan-400 dark:bg-cyan-950/35 dark:text-cyan-50"
															: "border-slate-200 bg-slate-50 hover:border-blue-300 dark:border-slate-800 dark:bg-slate-950/60 dark:hover:border-cyan-800",
													)}
												>
													<input
														type="radio"
														name="compression-goal"
														value={item.key}
														checked={selected}
														onChange={() => setGoal(item.key)}
														className="sr-only"
													/>
													<span className="flex items-center justify-between gap-2">
														<Icon className="size-4" aria-hidden="true" />
														{selected ? (
															<Check className="size-4" aria-hidden="true" />
														) : null}
													</span>
													<span className="text-sm font-semibold">
														{t(`goals.${item.key}`)}
													</span>
												</label>
											);
										})}
									</div>
								</fieldset>

								{goal === "target" ? (
									<div className="mt-5">
										<label
											htmlFor="target-size"
											className="text-sm font-semibold"
										>
											{t("target.label")}
										</label>
										<div className="relative mt-2">
											<input
												id="target-size"
												type="number"
												min="0.1"
												step="0.1"
												value={targetSize}
												onChange={(event) => setTargetSize(event.target.value)}
												aria-invalid={hasTargetError}
												aria-describedby={
													targetError
														? "target-size-error"
														: hasQualityWarning
															? "target-size-warning"
															: "target-size-help"
												}
												className="focus-visible:ring-ring h-11 w-full rounded-xl border border-slate-300 bg-white px-3 pr-12 text-sm font-semibold outline-none focus-visible:ring-2 dark:border-slate-700 dark:bg-slate-950"
											/>
											<span className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-semibold">
												MB
											</span>
										</div>
										{targetError ? (
											<p
												id="target-size-error"
												className="mt-2 flex items-start gap-1.5 text-xs font-medium text-red-700 dark:text-red-300"
												role="alert"
											>
												<AlertTriangle
													className="mt-px size-3.5 shrink-0"
													aria-hidden="true"
												/>
												{targetError}
											</p>
										) : hasQualityWarning ? (
											<p
												id="target-size-warning"
												className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium leading-5 text-amber-950 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-100"
											>
												<AlertTriangle
													className="mt-0.5 size-3.5 shrink-0"
													aria-hidden="true"
												/>
												{t("target.warning")}
											</p>
										) : (
											<p
												id="target-size-help"
												className="text-muted-foreground mt-2 text-xs"
											>
												{t("target.help")}
											</p>
										)}
									</div>
								) : null}

								<div className="mt-6 border-t border-slate-200 pt-5 dark:border-slate-800">
									<div>
										<label
											htmlFor="output-resolution"
											className="text-sm font-semibold"
										>
											{t("output.resolution")}
										</label>
										<select
											id="output-resolution"
											value={resolution}
											onChange={(event) => {
												const option = RESOLUTION_OPTIONS.find(
													(item) => item.value === event.target.value,
												);
												if (option) setResolution(option.value);
											}}
											disabled={phase !== "ready"}
											className="focus-visible:ring-ring mt-2 h-11 w-full cursor-pointer rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950"
										>
											{RESOLUTION_OPTIONS.map((option) => (
												<option
													key={option.value}
													value={option.value}
													disabled={
														option.value !== "original" &&
														sourceSize.height < option.height
													}
												>
													{option.value === "original" && sourceSize.width
														? t("output.originalDimensions", {
																width: sourceSize.width,
																height: sourceSize.height,
															})
														: option.value === "original"
															? t("output.original")
															: option.value}
												</option>
											))}
										</select>
									</div>

									<div className="mt-4 flex min-h-12 items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-3.5 dark:border-slate-800 dark:bg-slate-950/60">
										<label
											htmlFor="keep-audio"
											className="cursor-pointer text-sm font-semibold"
										>
											{t("output.keepAudio")}
										</label>
										<Switch
											id="keep-audio"
											checked={keepAudio}
											onCheckedChange={setKeepAudio}
											disabled={phase !== "ready"}
											aria-label={t("output.keepAudioAria")}
										/>
									</div>
								</div>

								<div className="mt-5 rounded-xl border border-cyan-200 bg-cyan-50/70 px-4 py-3 text-xs leading-5 text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100">
									<p className="flex items-start gap-2 font-medium">
										<ShieldCheck
											className="mt-0.5 size-3.5 shrink-0"
											aria-hidden="true"
										/>
										{t("privacy")}
									</p>
								</div>

								<Button
									ref={compressButtonRef}
									className="mt-5 h-12 w-full rounded-xl bg-blue-600 text-base text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500"
									disabled={!isReady}
									onClick={handleCompress}
								>
									<FileDown className="size-4.5" aria-hidden="true" />
									{t("actions.compress")}
								</Button>
								<p
									className="text-muted-foreground mt-2 min-h-5 text-center text-xs"
									aria-live="polite"
								>
									{disabledReason ?? t("status.ready")}
								</p>
							</div>
						</div>
					)}
				</div>
			</div>

			{phase === "processing" ? (
				<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
					<div className="mb-4 flex items-center justify-between">
						<div>
							<h3 className="font-bold">{t("processing.title")}</h3>
							<p className="text-muted-foreground mt-1 text-sm">
								{t("processing.pass", { pass: progress.pass })}
							</p>
						</div>
						<Button
							variant="outline"
							className="h-10 rounded-xl"
							onClick={() => abortRef.current?.abort()}
						>
							{t("processing.cancel")}
						</Button>
					</div>
					<div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
						<div
							className="h-full bg-blue-600 transition-all duration-300"
							style={{ width: `${progress.percent}%` }}
						/>
					</div>
					<p className="text-muted-foreground mt-2 text-xs">
						{t("processing.progress", { progress: progress.percent })}
					</p>
				</div>
			) : null}

			{phase === "success" || phase === "partial" ? (
				<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
					<div className="mb-4 flex items-start justify-between">
						<div>
							<h3 className="font-bold">{t("success.title")}</h3>
							<p className="text-muted-foreground mt-1 text-sm">
								{phase === "partial"
									? t("success.detailsPartial", {
											size: formatBytes({ bytes: resultSize, locale }),
										})
									: t("success.details", {
											size: formatBytes({ bytes: resultSize, locale }),
											width: resultResolution.width,
											height: resultResolution.height,
										})}
							</p>
						</div>
						<div className="flex gap-2">
							<Button
								variant="outline"
								className="h-10 rounded-xl"
								onClick={() => {
									setPhase("ready");
									setResultBlob(null);
									if (resultUrl) {
										URL.revokeObjectURL(resultUrl);
										setResultUrl(null);
									}
								}}
							>
								{t("actions.adjust")}
							</Button>
							<Button
								variant="ghost"
								className="h-10 rounded-xl"
								onClick={resetFile}
							>
								{t("actions.newVideo")}
							</Button>
						</div>
					</div>

					<div className="relative aspect-video min-h-56 overflow-hidden rounded-2xl bg-slate-950">
						{phase === "partial" ? (
							<div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-200">
								<AlertTriangle
									className="size-7 text-amber-300"
									aria-hidden="true"
								/>
								<p className="max-w-md text-sm leading-6">
									{t("success.previewUnavailable")}
								</p>
							</div>
						) : resultUrl ? (
							// User-provided videos do not include a separate captions track.
							// eslint-disable-next-line jsx-a11y/media-has-caption
							<video
								src={resultUrl}
								controls
								className="h-full w-full object-contain"
							/>
						) : null}
					</div>

					<div className="mt-4 grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-950/60">
						<div>
							<span className="text-muted-foreground block text-xs">
								{t("metrics.original")}
							</span>
							<strong className="mt-1 block">
								{formatBytes({ bytes: file?.size ?? 0, locale })}
							</strong>
						</div>
						<div>
							<span className="text-muted-foreground block text-xs">
								{t("metrics.result")}
							</span>
							<strong className="mt-1 block">
								{formatBytes({ bytes: resultSize, locale })}
							</strong>
						</div>
					</div>

					<Button
						className="mt-4 h-12 w-full rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500"
						disabled={!resultUrl}
						onClick={() => {
							if (!resultUrl || !resultBlob || !file) return;
							const a = document.createElement("a");
							a.href = resultUrl;
							const baseName = file.name.replace(/\.[^/.]+$/, "");
							a.download = `${baseName}-compressed.mp4`;
							a.click();
						}}
					>
						<FileDown className="mr-2 size-4.5" aria-hidden="true" />
						{t("actions.download")}
					</Button>
				</div>
			) : null}

			{phase === "failed" ? (
				<div className="rounded-2xl border border-red-200 bg-red-50 p-5 shadow-sm dark:border-red-900 dark:bg-red-950/40">
					<div className="mb-4 flex items-start gap-3">
						<AlertTriangle
							className="mt-1 size-6 shrink-0 text-red-600 dark:text-red-400"
							aria-hidden="true"
						/>
						<div>
							<h3 className="font-bold text-red-900 dark:text-red-200">
								{t("failure.title")}
							</h3>
							<p className="mt-1 text-sm text-red-800 dark:text-red-300">
								{t(`failure.${errorCategory}`)}
							</p>
						</div>
					</div>
					<div className="flex gap-2">
						<Button
							variant="outline"
							className="h-10 rounded-xl border-red-300 bg-white text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-red-950/60 dark:text-red-200"
							onClick={() => {
								setPhase("ready");
							}}
						>
							{t("actions.retry")}
						</Button>
						<Button
							variant="outline"
							className="h-10 rounded-xl"
							onClick={() => {
								setPhase("ready");
							}}
						>
							{t("actions.adjust")}
						</Button>
						<Button
							variant="ghost"
							className="h-10 rounded-xl"
							onClick={resetFile}
						>
							{t("actions.newVideo")}
						</Button>
					</div>
				</div>
			) : null}

			{phase === "cancelled" ? (
				<div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/60">
					<div className="mb-4">
						<h3 className="font-bold">{t("cancelled.title")}</h3>
						<p className="text-muted-foreground mt-1 text-sm">
							{t("cancelled.description")}
						</p>
					</div>
					<div className="flex gap-2">
						<Button
							variant="outline"
							className="h-10 rounded-xl"
							onClick={() => {
								setPhase("ready");
							}}
						>
							{t("actions.adjust")}
						</Button>
						<Button
							variant="ghost"
							className="h-10 rounded-xl"
							onClick={resetFile}
						>
							{t("actions.newVideo")}
						</Button>
					</div>
				</div>
			) : null}
		</section>
	);
}
